import fsp from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import readline from "node:readline";
import {fileURLToPath} from "node:url";
import {chromium, type Browser} from "playwright";
import {createServer, type ViteDevServer} from "vite";
import {NodeFsDuplex} from "./node.js";
import {superjson} from "./superjson.js";

function askQuestion(query: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve) =>
    rl.question(query, (ans) => {
      rl.close();
      resolve(ans);
    }),
  );
}

async function main() {
  console.log("--- FsDuplex E2E Manual Verification ---");

  const sharedDir = path.join(os.tmpdir(), `fs-duplex-manual-e2e-${crypto.randomUUID()}`);
  await fsp.mkdir(sharedDir, {recursive: true});

  console.log("\n!!! ACTION REQUIRED !!!");
  console.log("------------------------------------------------------------------");
  console.log("A browser window will open. Please perform the following steps:");
  console.log("1. Click the 'Initialize FsDuplex' button on the webpage.");
  console.log("2. In the dialog that opens, navigate to the following directory:");
  console.log(`   \x1b[32m${sharedDir}\x1b[0m`);
  console.log("3. Select it and grant access.");
  console.log("------------------------------------------------------------------");
  await askQuestion("Press ENTER to continue once you have read the instructions...");

  const taskFilepathPrefix = path.join(sharedDir, "task");
  const prefixBasename = path.basename(taskFilepathPrefix);

  let viteServer: ViteDevServer;
  let browser: Browser | undefined;

  try {
    viteServer = await createServer({
      root: path.dirname(fileURLToPath(import.meta.url)),
      server: {port: 0},
      logLevel: "silent",
    });
    await viteServer.listen();
    const serverPort = viteServer.config.server.port!;
    const origin = `http://localhost:${serverPort}`;

    browser = await chromium.launch({headless: false});
    const page = await browser.newPage();

    const browserOpenPromise = new Promise<void>((resolve) => {
      page.on("console", (msg) => {
        const text = msg.text();
        console.log(`[Browser Console] ${text}`);
        if (text.startsWith("__HARNESS_EVENT__")) {
          try {
            const eventData = JSON.parse(text.substring("__HARNESS_EVENT__:".length));
            if (eventData.event === "open") {
              console.log("[Verification] Browser side is OPEN.");
              resolve();
            }
          } catch {}
        }
      });
    });

    await page.goto(`${origin}/E2E-browser-harness.html`);
    await page.waitForFunction(() => window.harness);

    await page.evaluate((prefix) => {
      const button = document.getElementById("action-button");
      const header = document.getElementById("status-header");
      if (button && header) {
        button.onclick = () => window.harness.initialize(prefix);
        button.innerText = "Initialize FsDuplex";
        header.innerText = "Manual Verification: Ready";
      }
    }, prefixBasename);

    console.log("\n[Verification] Node.js Initiator starting...");
    const initiator = new NodeFsDuplex("initiator", superjson, taskFilepathPrefix);
    const initiatorOpenPromise = initiator.onOpen.once();
    await initiator.start();
    initiator.init({message: "Hello from Node!"});

    console.log("[Verification] Waiting for handshake to complete (requires your manual action in browser)...");

    await Promise.all([initiatorOpenPromise, browserOpenPromise]);

    console.log("\n✅ SUCCESS: Handshake completed successfully!");

    const nodeState = initiator.currentState;
    const browserState = await page.evaluate(() => window.harness.duplex!.currentState);

    console.log(`[Verification] Final states: Node='${nodeState}', Browser='${browserState}'`);
    if (nodeState !== "open" || browserState !== "open") {
      throw new Error("Final states are not 'open'.");
    }
  } catch (error: any) {
    console.error("\n--- An error occurred during the script execution ---");
    console.error(error);
  } finally {
    console.log("\n[Verification] Cleaning up resources...");
    await browser?.close();
    await viteServer?.close();
    await fsp.rm(sharedDir, {recursive: true, force: true});
    console.log("[Verification] Cleanup complete.");
  }
}

main();
